import { PlatformBasicError, PlatformFieldError } from '@cloudquery/api-platform';
import { FilterColumnsArgs, GetColumnForNewFilterArgs, GridColDef } from '@mui/x-data-grid';

import { AssetResourceCategoryMenuItem } from '../types';

export const recordCount = (recordCount: number) =>
  new Intl.NumberFormat('en', { notation: 'standard' }).format(recordCount ?? 0);

export const countFormat = (count: number) =>
  new Intl.NumberFormat('en', { notation: 'compact' }).format(count ?? 0);

export const filterColumns = ({ columns, currentFilters, field }: FilterColumnsArgs) => {
  // remove already filtered fields from list of columns
  const filteredFields = currentFilters?.map((item) => item.field);

  return columns
    .filter(
      (colDef) =>
        colDef.filterable && (colDef.field === field || !filteredFields.includes(colDef.field)),
    )
    .map((column) => column.field);
};

export const getColumnForNewFilter = ({ columns, currentFilters }: GetColumnForNewFilterArgs) => {
  const filteredFields = currentFilters?.map(({ field }) => field);
  const columnForNewFilter = columns
    .filter((colDef) => colDef.filterable && !filteredFields.includes(colDef.field))
    .find((colDef) => colDef.filterOperators?.length);

  return columnForNewFilter?.field ?? null;
};

export const filterSidebarList = (
  resourceCategories: AssetResourceCategoryMenuItem[],
  menuSearchTerm: string,
) => {
  const filteredList = [];
  for (const category of resourceCategories) {
    const filteredSubItems =
      category.subitems?.filter(({ label }) =>
        label.toLowerCase().includes(menuSearchTerm.toLowerCase()),
      ) ?? [];

    if (filteredSubItems.length > 0) {
      filteredList.push({ ...category, subitems: filteredSubItems });
    } else if (category.label.toLowerCase().includes(menuSearchTerm.toLowerCase())) {
      filteredList.push(category);
    }
  }

  return filteredList.sort((a, b) => {
    // Null value means "All" and should be at the bottom of the list
    if (a.value === null && b.value !== null) {
      return 1;
    }

    if (a.value !== null && b.value === null) {
      return -1;
    }

    return a.label.localeCompare(b.label);
  });
};

export const tryParseJson = (rawJson?: Record<string, any>) => {
  const newJson: Record<string, any> = {};

  if (rawJson) {
    for (const key of Object.keys(rawJson)) {
      try {
        newJson[key] = JSON.parse(rawJson[key] as any);
      } catch {
        newJson[key] = rawJson[key];
      }
    }
  }

  return newJson;
};

export const extractErrorMessage = (
  error: PlatformBasicError | PlatformFieldError | null,
): string | undefined => {
  return error?.status === 400
    ? ((error as any)?.data?.field_errors?.filter ??
        (error as any)?.data?.field_errors?.filter_id ??
        (error as any)?.data?.field_errors?.query ??
        error?.message)
    : undefined;
};

export const getDefaultColumnMinWidth = (columnName: string, max?: number) =>
  Math.max(columnName.length * 11 + 16, max ?? 150);

export const getDefaultColumnMaxWidth = (max?: number) => max ?? 500;

export const getDefaultColumns = (
  columns: GridColDef[],
  cloudAssetColumns: GridColDef[],
  schemaDefaultColumns?: string[],
): string[] => {
  const resolvedSchemaDefaultColumns = schemaDefaultColumns ?? [];

  const cloudAssetColumnNames = cloudAssetColumns.map(({ field }) => field);

  const resourceSpecificColumns = (
    resolvedSchemaDefaultColumns.length > 0
      ? resolvedSchemaDefaultColumns
      : columns.map(({ field }) => field)
  ).filter((field) => !cloudAssetColumnNames.includes(field));

  // TODO: artificially limit the number of columns to 10 for now -- BE should give us a reasonable default list
  return [...cloudAssetColumnNames, ...resourceSpecificColumns].slice(0, 10);
};

export const orderColumns = (
  columns: GridColDef[],
  cloudAssetColumns: GridColDef[],
  schemaDefaultColumns?: string[],
): GridColDef[] => {
  const resolvedSchemaDefaultColumns = schemaDefaultColumns ?? [];

  const nonDefaultColumns = columns.filter(
    ({ field }) => !resolvedSchemaDefaultColumns.includes(field),
  );

  const defaultColumns = resolvedSchemaDefaultColumns
    .map((defaultCol) => {
      const column = columns.find(({ field }) => field === defaultCol);

      return column;
    })
    .filter(Boolean) as GridColDef[];

  const cloudAssetColumnNames = new Set(cloudAssetColumns.map(({ field }) => field));

  const resourceSpecificColumns = [...defaultColumns, ...nonDefaultColumns].filter(
    ({ field }) => !cloudAssetColumnNames.has(field),
  );

  return [...cloudAssetColumns, ...resourceSpecificColumns];
};

export const getHeaderName = (fieldName: string) => fieldName.replaceAll('_', ' ').toUpperCase();
